
import React, { useEffect, useState, useRef } from 'react';
import QRCode from 'qrcode';
import { postLog } from '../utils/authApi';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

const APP_URL = window.location.origin;

interface Order {
  token: string;
  status: 'waiting' | 'ready' | 'collected';
  customer_name?: string;
  order_note?: string;
  created_at: string;
}

export default function KitchenDashboard() {
  const outletToken = new URLSearchParams(window.location.search).get('outlet_token');
  const [orders, setOrders] = useState<Order[]>(() => {
    try {
      const saved = localStorage.getItem('cloudairambo_kitchen_last_orders');
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  });
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNote, setNewNote] = useState('');
  const [creating, setCreating] = useState(false);
  const [qrModal, setQrModal] = useState<{ token: string; url: string; qr: string } | null>(null);
  const [filter, setFilter] = useState<'all' | 'waiting' | 'ready' | 'collected'>('all');
  const [orderView, setOrderView] = useState<'dinein' | 'counter'>('counter');
  const [searchQuery, setSearchQuery] = useState('');
  const [itemsModal, setItemsModal] = useState<{ token: string; customerName: string; items: { name: string; qty: number; special: string[] }[] } | null>(null);
  const [newItemDraft, setNewItemDraft] = useState('');
  const [checkedItems, setCheckedItems] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('cloudairambo_kitchen_checked_items');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  // Backend doesn't return a ready/collected timestamp — only created_at.
  // Track those transitions locally so we can report avg turnaround times.
  const [orderTimings, setOrderTimings] = useState<Record<string, { readyAt?: number; collectedAt?: number }>>(() => {
    try {
      const saved = localStorage.getItem('cloudairambo_kitchen_order_timings');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const recordTiming = (token: string, key: 'readyAt' | 'collectedAt') => {
    setOrderTimings(prev => {
      const next = { ...prev, [token]: { ...prev[token], [key]: Date.now() } };
      localStorage.setItem('cloudairambo_kitchen_order_timings', JSON.stringify(next));
      return next;
    });
  };

  // Snapshot of how many parsed items existed the moment this order was
  // last marked Ready. Any item at an index beyond this, seen after the
  // order reopens to 'waiting', is something added post-Ready — i.e. the
  // customer ordered more after the first batch already went out.
  const [readyItemCounts, setReadyItemCounts] = useState<Record<string, number>>(() => {
    try {
      const saved = localStorage.getItem('cloudairambo_kitchen_ready_counts');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const recordReadyItemCount = (token: string, count: number) => {
    setReadyItemCounts(prev => {
      const next = { ...prev, [token]: count };
      localStorage.setItem('cloudairambo_kitchen_ready_counts', JSON.stringify(next));
      return next;
    });
  };

  // Parses "1x Rihan, 1x Rihan2, 1x rihan3" into structured line items.
  // Falls back to treating the whole note as one unstructured item if it
  // doesn't match the "qty x name" pattern the POS cart saves it in.
  // Only surface note fragments that look like a special request (spicy,
  // no-onion, allergy, extra sauce, etc.) on the order card — the full note
  // still goes to parseOrderItems below for the item checklist, untouched.
  const NOTE_KEYWORDS = ['spicy', 'extra', 'no ', 'less ', 'mild', 'allerg', 'sauce', 'ice', 'sugar', 'salt', 'vegan', 'vegetarian', 'gluten', 'dairy', 'nut', 'urgent', 'rush', 'hot', 'well done', 'onion', 'cheese', 'halal', 'spice'];

  const extractSpecialNotes = (note?: string): string[] => {
    if (!note?.trim()) return [];
    return note.split(',').map(p => p.trim()).filter(part => {
      const lower = part.toLowerCase();
      return NOTE_KEYWORDS.some(k => lower.includes(k));
    });
  };

  const parseOrderItems = (note?: string): { name: string; qty: number; special: string[] }[] => {
    if (!note?.trim()) return [];
    return note.split(',').map(part => {
      const trimmed = part.trim();
      const match = trimmed.match(/^(\d+)\s*x\s*(.+)$/i);
      const raw = match ? match[2].trim() : trimmed;
      const qty = match ? (parseInt(match[1], 10) || 1) : 1;
      const lower = raw.toLowerCase();
      const special = NOTE_KEYWORDS.filter(k => lower.includes(k));
      return { name: raw, qty, special };
    }).filter(i => i.name);
  };

  const buildNoteFromItems = (items: { name: string; qty: number }[]) =>
    items.map(i => `${i.qty}x ${i.name}`).join(', ');

  const patchOrderNote = async (token: string, order_note: string) => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const res = await fetch(`${BACKEND}/tokens/${token}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
      body: JSON.stringify({ order_note }),
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
  };

  const patchAppendItem = async (token: string, append_note: string) => {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const res = await fetch(`${BACKEND}/tokens/${token}/items`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
      body: JSON.stringify({ append_note }),
    });
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
  };

  // A remove/qty edit reshuffles item positions, which invalidates the
  // index-based checkedItems keys for this order — clear them rather than
  // risk a checkmark landing on the wrong item afterward.
  const clearChecksForToken = (token: string) => {
    setCheckedItems(prev => {
      const next = { ...prev };
      Object.keys(next).filter(k => k.startsWith(`${token}:`)).forEach(k => delete next[k]);
      localStorage.setItem('cloudairambo_kitchen_checked_items', JSON.stringify(next));
      return next;
    });
  };

  const syncOrderNote = (token: string, order_note: string) => {
    setOrders(prev => prev.map(o => o.token === token ? { ...o, order_note } : o));
    setItemsModal(prev => prev && prev.token === token ? { ...prev, items: parseOrderItems(order_note) } : prev);
  };

  const addItemToOrder = async (payload: { token: string; name: string; qty: number }) => {
    try {
      const data = await patchAppendItem(payload.token, `${payload.qty}x ${payload.name}`);
      syncOrderNote(payload.token, data.order_note);
      postLog('KITCHEN_ORDER_ITEM_ADDED', `Token ${payload.token} | +${payload.qty}x ${payload.name}`);
    } catch (e) {
      alert(`Couldn't add item to Token ${payload.token}. Check your connection and try again.`);
    }
  };

  const removeItemFromOrder = async (token: string, idx: number) => {
    if (!itemsModal) return;
    const note = buildNoteFromItems(itemsModal.items.filter((_, i) => i !== idx));
    try {
      const data = await patchOrderNote(token, note);
      clearChecksForToken(token);
      syncOrderNote(token, data.order_note);
      postLog('KITCHEN_ORDER_ITEM_REMOVED', `Token ${token} | removed item`);
    } catch (e) {
      alert(`Couldn't update Token ${token}. Check your connection and try again.`);
    }
  };

  const changeItemQty = async (token: string, idx: number, delta: number) => {
    if (!itemsModal) return;
    const note = buildNoteFromItems(itemsModal.items.map((it, i) => i === idx ? { ...it, qty: Math.max(1, it.qty + delta) } : it));
    try {
      const data = await patchOrderNote(token, note);
      clearChecksForToken(token);
      syncOrderNote(token, data.order_note);
    } catch (e) {
      alert(`Couldn't update Token ${token}. Check your connection and try again.`);
    }
  };

  const toggleChecked = (key: string) => {
    setCheckedItems(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem('cloudairambo_kitchen_checked_items', JSON.stringify(next));
      return next;
    });
  };

  // All items checked just triggers a visual pulse on the Ready button —
  // it does NOT auto-mark the order ready or auto-close the modal.
  // Staff must still tap Ready themselves; this only nudges them to.
  const [modalComplete, setModalComplete] = useState(false);
  const [modalClosing, setModalClosing] = useState(false);

  const closeModal = (afterCloseFn) => {
    setModalClosing(true);
    setTimeout(() => {
      afterCloseFn?.();
      setItemsModal(null);
      setModalClosing(false);
      setModalComplete(false);
    }, 200); // matches modalFadeOut duration below
  };

  useEffect(() => {
    if (!itemsModal || itemsModal.items.length === 0) { setModalComplete(false); return; }
    const allChecked = itemsModal.items.every((item, idx) =>
      checkedItems[`${itemsModal.token}:${idx}:${item.name}`]
    );
    setModalComplete(allChecked);
  }, [checkedItems, itemsModal]);
const [authRole, setAuthRole] = useState<string | null>(null);
const [authName, setAuthName] = useState<string>('');

useEffect(() => {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  if (!jwt) { setAuthRole(''); return; }
  fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${jwt}` } })
    .then(r => r.json())
    .then(user => {
      setAuthRole(user?.role || 'viewer');
      setAuthName(user?.name || user?.email || 'Staff');
    })
    .catch(() => setAuthRole(''));
}, []);

const canManageOrders = ['owner','admin','manager','staff','cashier'].includes(authRole || '');
  const [outletError, setOutletError] = useState(false);

  // "Claim" tag so two staff on the same screen don't both start working an
  // order. NOTE: this is local-only — it syncs instantly across tabs/staff
  // on the SAME device/screen, but won't be visible on a separate device
  // until the backend stores + returns a claimed_by field (see note below).
  const [claims, setClaims] = useState<Record<string, { name: string; ts: number }>>(() => {
    try {
      const saved = localStorage.getItem('cloudairambo_kitchen_claims');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const [claimPrompt, setClaimPrompt] = useState<{ token: string } | null>(null);
  const [claimInfoModal, setClaimInfoModal] = useState<{ token: string; name: string; order: Order } | null>(null);
  const [claimNameInput, setClaimNameInput] = useState('');

  // Deliberately NOT pre-filled and NOT remembered — if the box auto-fills
  // with the last name used, anyone can claim under someone else's name by
  // just hitting Enter without typing anything. Forcing a blank box each
  // time means the name typed is always something the person entered themselves.
  const openClaimPrompt = (token: string) => {
    setClaimNameInput('');
    setClaimPrompt({ token });
  };

  const confirmClaim = () => {
    if (!claimPrompt) return;
    const name = claimNameInput.trim();
    if (!name) return;
    setClaims(prev => {
      const next = { ...prev, [claimPrompt.token]: { name, ts: Date.now() } };
      localStorage.setItem('cloudairambo_kitchen_claims', JSON.stringify(next));
      return next;
    });
    setClaimPrompt(null);
  };

  const releaseOrder = (token: string) => {
    setClaims(prev => {
      const next = { ...prev };
      delete next[token];
      localStorage.setItem('cloudairambo_kitchen_claims', JSON.stringify(next));
      return next;
    });
  };

  // Auto-clear claims AND item-checked ticks once an order is collected —
  // nothing left to claim or pack, and if this token gets reused later it
  // shouldn't show old checkmarks from a previous, finished order.
  useEffect(() => {
    const collectedTokens = new Set(orders.filter(o => o.status === 'collected').map(o => o.token));
    if (collectedTokens.size === 0) return;

    if (Object.keys(claims).some(t => collectedTokens.has(t))) {
      setClaims(prev => {
        const next = { ...prev };
        Object.keys(next).forEach(t => { if (collectedTokens.has(t)) delete next[t]; });
        localStorage.setItem('cloudairambo_kitchen_claims', JSON.stringify(next));
        return next;
      });
    }

    // Checkmarks are deliberately NOT cleared here anymore. Appends never
    // reorder existing items (new items are always added at the end), so
    // old checkmarks stay correctly attached to the right item even if
    // this ticket gets reopened later — which is exactly what lets a
    // reopened order show "4 of 6 already packed" instead of resetting
    // to 0 and making the kitchen redo work that's already done.
  }, [orders]);

  const loadOrders = async (silent = false) => {
    // No outlet_token in the URL — never fall back to the unscoped /tokens
    // endpoint, which returns orders across every outlet in the org. Show
    // the "select an outlet" state instead of leaking cross-outlet data.
    if (!outletToken) {
      setOutletError(true);
      setOrders([]);
      return;
    }
    // Never block the UI on this fetch — cached orders (if any) are already
    // painted from the lazy useState init above, so this just refreshes quietly.
    try {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const res = await fetch(`${BACKEND}/tokens?outlet_token=${outletToken}`, {
        headers: jwt ? { Authorization: `Bearer ${jwt}` } : {},
      });
      if (!res.ok) {
        setOutletError(true);
        setOrders([]);
        return;
      }
      setOutletError(false);
      const data = await res.json();
      const list = Array.isArray(data)
        ? data
        : (Array.isArray(data?.tokens) ? data.tokens : []);
      setOrders(list);
      localStorage.setItem('cloudairambo_kitchen_last_orders', JSON.stringify(list));
    } catch (e) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
    const interval = setInterval(() => loadOrders(true), 5000);
    return () => clearInterval(interval);
  }, []);

  const createOrder = async () => {
    setCreating(true);
    try {
      const jwt = localStorage.getItem('cloudairambo_jwt') || '';
      const res = await fetch(`${BACKEND}/tokens/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({
          customer_name: newName || undefined,
          order_note: newNote || undefined,
          outlet_token: outletToken,
        }),
      });
      const data = await res.json();
      postLog('KITCHEN_ORDER_CREATED', `Token ${data.token} created${newName ? ` | Customer: ${newName}` : ' | Guest'}${newNote ? ` | Note: ${newNote}` : ''}`);
      setNewName(''); setNewNote('');

      // Generate QR
      const url = `${APP_URL}/wait?token=${data.token}`;
      const qr = await QRCode.toDataURL(url, { width: 300, margin: 2 });
      setQrModal({ token: data.token, url, qr });
      loadOrders();
    } catch (e) {}
    finally { setCreating(false); }
  };

  const updateStatus = async (token: string, status: string) => {
    const order = orders.find(o => o.token === token);
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';

    // Optimistic update — flip it in the UI immediately so a click always
    // feels like it did something, instead of silently waiting on the network.
    const prevOrders = orders;
    setOrders(cur => cur.map(o => o.token === token ? { ...o, status: status as Order['status'] } : o));

    try {
      const res = await fetch(`${BACKEND}/tokens/${token}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}) },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`${res.status} ${res.statusText}${body ? ` — ${body}` : ''}`);
      }
      if (status === 'ready') {
        recordTiming(token, 'readyAt');
        recordReadyItemCount(token, parseOrderItems(order?.order_note).length);
      }
      if (status === 'collected') recordTiming(token, 'collectedAt');
      postLog('KITCHEN_ORDER_STATUS', `Token ${token} → ${status.toUpperCase()}${order?.customer_name ? ` | ${order.customer_name}` : ''}${order?.order_note ? ` | ${order.order_note}` : ''}`);
      loadOrders(true);
    } catch (e) {
      // Roll back the optimistic change and tell you it actually failed,
      // instead of leaving the screen looking unchanged with no explanation.
      setOrders(prevOrders);
      alert(`Couldn't update Token ${token} to ${status}. ${e instanceof Error ? e.message : 'Check your connection and try again.'}`);
    }
  };

  const showQR = async (token: string) => {
    const url = `${APP_URL}/wait?token=${token}`;
    const qr = await QRCode.toDataURL(url, { width: 300, margin: 2 });
    setQrModal({ token, url, qr });
  };

  const safeOrders = Array.isArray(orders) ? orders : [];

  const filtered = safeOrders.filter(o =>
    (filter === 'all' || o.status === filter) &&
    (searchQuery.trim() === '' || o.token.toLowerCase().includes(searchQuery.trim().toLowerCase()))
  );

  // Dine-in table orders are tagged customer_name === "Table {number}" by
  // TableLayoutView's KOT push — split them into their own section so
  // waitstaff aren't hunting for table orders mixed in with counter/pickup ones.
  const isTableOrder = (o: Order) => /^Table\s/.test(o.customer_name || '');
  const dineInOrders = filtered.filter(isTableOrder);
  const counterOrders = filtered.filter(o => !isTableOrder(o));

  // If a fresh dine-in order shows up while staff are on the Counter tab,
  // jump them over automatically so a table order never gets missed just
  // because the tab wasn't active — but only nudge on NEW waiting orders,
  // not every poll (so it doesn't yank someone back mid-task on 'ready' etc).
  const prevDineInWaitingCount = React.useRef(0);
  useEffect(() => {
    const waitingNow = dineInOrders.filter(o => o.status === 'waiting').length;
    if (waitingNow > prevDineInWaitingCount.current && orderView !== 'dinein') {
      setOrderView('dinein');
    }
    prevDineInWaitingCount.current = waitingNow;
  }, [dineInOrders.length]);

  // Ready button in the list should only go green once every parsed item
  // for that order is checked off — orders with no parseable items (no
  // checklist to speak of) are treated as always packable.
  const getPackStatus = (order: Order) => {
    const items = parseOrderItems(order.order_note);
    if (items.length === 0) return { total: 0, packed: 0, complete: true };
    const packed = items.filter((item, idx) => checkedItems[`${order.token}:${idx}:${item.name}`]).length;
    return { total: items.length, packed, complete: packed === items.length };
  };

  const isToday = (iso: string) => {
    const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z');
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
  };

  const todayOrders = safeOrders.filter(o => isToday(o.created_at));
  const todayCollected = todayOrders.filter(o => o.status === 'collected');
  const todayReady = todayOrders.filter(o => o.status === 'ready');
  const todayWaiting = todayOrders.filter(o => o.status === 'waiting');

  const avgMinutes = (list: Order[], key: 'readyAt' | 'collectedAt') => {
    const durations = list
      .map(o => {
        const endTs = orderTimings[o.token]?.[key];
        if (!endTs) return null;
        const startTs = new Date(o.created_at.endsWith('Z') || o.created_at.includes('+') ? o.created_at : o.created_at + 'Z').getTime();
        return (endTs - startTs) / 60000;
      })
      .filter((v): v is number => v !== null && v >= 0);
    return durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length : null;
  };

  const avgPrepMins = avgMinutes(todayOrders, 'readyAt');
  const avgTotalMins = avgMinutes(todayOrders, 'collectedAt');

  const STATUS_STYLE: Record<string, { bg: string; text: string; dot: string; label: string }> = {
    waiting: { bg: '#fef9ec', text: '#b45309', dot: '#f59e0b', label: 'Waiting' },
    ready:   { bg: '#f0fdf4', text: '#15803d', dot: '#22c55e', label: 'Ready' },
    collected: { bg: '#f5f3ff', text: '#6d28d9', dot: '#8b5cf6', label: 'Collected' },
  };

  const renderOrderCard = (order: Order) => {
    const isPreparing = order.status === 'waiting' && !!claims[order.token];
    const st = isPreparing
      ? { bg: '#eff6ff', text: '#2563eb', dot: '#3b82f6', label: 'Preparing' }
      : (STATUS_STYLE[order.status] || STATUS_STYLE.waiting);
    // A waiting ticket with some items already packed means a new round
    // got appended mid-prep, reopening it — not a fresh order arriving.
    const cardPack = getPackStatus(order);
    const isReopened = order.status === 'waiting' && cardPack.total > 0 && cardPack.packed > 0 && !cardPack.complete;
    return (
      <div key={order.token} style={{ background: '#fff', borderRadius: 16, padding: '14px 16px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>

        {/* Token */}
        <div
          onClick={() => setItemsModal({
            token: order.token,
            customerName: order.customer_name || 'Guest',
            items: parseOrderItems(order.order_note),
          })}
          title="View order items"
          style={
            order.status === 'waiting'
              ? {
                  fontSize: 18, fontWeight: 900, letterSpacing: 1.5, minWidth: 62,
                  textAlign: 'center', cursor: 'pointer', borderRadius: 8,
                  border: '1px solid #dc2626', padding: '5px 8px',
                  animation: 'tokenBlink 1.3s steps(1) infinite',
                }
              : {
                  fontSize: 18, fontWeight: 900, letterSpacing: 1.5, minWidth: 62,
                  textAlign: 'center', cursor: 'pointer', borderRadius: 8,
                  border: '1px solid #bbf7d0', padding: '5px 8px',
                  background: '#f0fdf4', color: '#15803d',
                }
          }
        >
          {order.token}
          <div style={{ fontSize: 8, fontWeight: 700, marginTop: 1 }}>
            {order.status === 'waiting' ? '▾ tap' : '✓ done'}
          </div>
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{order.customer_name || 'Guest'}</div>
          {extractSpecialNotes(order.order_note).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
              {extractSpecialNotes(order.order_note).map((n, i) => (
                <span key={i} style={{ background: '#fef2f2', color: '#dc2626', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, border: '1px solid #fecaca' }}>
                  ⚠ {n}
                </span>
              ))}
            </div>
          )}
          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>{new Date(order.created_at.endsWith('Z') || order.created_at.includes('+') ? order.created_at : order.created_at + 'Z').toLocaleTimeString()}</div>
        </div>

        {/* Status badge */}
        <div style={{ background: st.bg, color: st.text, borderRadius: 20, padding: '4px 12px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: st.dot }} />
          {st.label}
        </div>
        {isReopened && (
          <div
            title={`${cardPack.packed} of ${cardPack.total} already packed before this round was added`}
            style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa', borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            🔁 New items added
          </div>
        )}

        {/* Claim tag */}
        {order.status !== 'collected' && (
          claims[order.token] ? (
            <div
              onClick={() => setClaimInfoModal({ token: order.token, name: claims[order.token].name, order })}
              title={`Being handled by ${claims[order.token].name} — click for options`}
              style={{
                background: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe',
                borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 700,
                whiteSpace: 'nowrap', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
              }}>
              🔒 {claims[order.token].name}
            </div>
          ) : (
            <button onClick={() => openClaimPrompt(order.token)}
              style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 700, cursor: 'pointer', color: '#94a3b8', whiteSpace: 'nowrap' }}>
              Claim
            </button>
          )
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
          <button onClick={() => showQR(order.token)}
            style={{ background: '#f5f3ff', border: 'none', borderRadius: 8, padding: '7px 10px', fontSize: 12, cursor: 'pointer', fontWeight: 700, color: '#6366f1' }}>
            📱 QR
          </button>
          {canManageOrders && order.status === 'waiting' && (() => {
            const pack = getPackStatus(order);
            return (
              <button
                onClick={() => {
                  if (!pack.complete) {
                    setItemsModal({
                      token: order.token,
                      customerName: order.customer_name || 'Guest',
                      items: parseOrderItems(order.order_note),
                    });
                    return;
                  }
                  updateStatus(order.token, 'ready');
                }}
                title={pack.complete ? 'Mark ready' : `${pack.packed} of ${pack.total} packed — open checklist`}
                style={{
                  background: pack.complete ? '#22c55e' : '#e2e8f0',
                  color: pack.complete ? '#fff' : '#94a3b8',
                  border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12,
                  cursor: 'pointer', fontWeight: 700,
                }}>
                {pack.complete ? '✅ Ready' : `${pack.packed}/${pack.total} packed`}
              </button>
            );
          })()}
          {canManageOrders && order.status === 'ready' && (
            <button onClick={() => updateStatus(order.token, 'collected')}
              style={{ background: '#8b5cf6', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 12, cursor: 'pointer', fontWeight: 700, color: '#fff' }}>
              ✓ Collected
            </button>
          )}
        </div>
      </div>
    );
  };

  if (outletError) {
    return (
      <div style={{
        fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        background: '#f1f5f9',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🏬</div>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#0f172a' }}>Select an outlet</div>
          <div style={{ fontSize: 13, marginTop: 6, maxWidth: 280 }}>You manage multiple outlets — please open Kitchen Dashboard from the POS screen so the correct outlet is selected.</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      background: '#f1f5f9',
      minHeight: '100vh',
      padding: 20
    }}>
      
      {/* Header */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '16px 20px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a' }}>📋 Order Management Dashboard</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>Token Order Management</div>
        </div>
        <button onClick={() => loadOrders(true)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 10, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#475569' }}>
          🔄 Refresh
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Waiting', count: safeOrders.filter(o => o.status === 'waiting').length, color: '#f59e0b', bg: '#fef9ec' },
          { label: 'Ready', count: safeOrders.filter(o => o.status === 'ready').length, color: '#22c55e', bg: '#f0fdf4' },
          { label: 'Collected', count: safeOrders.filter(o => o.status === 'collected').length, color: '#8b5cf6', bg: '#f5f3ff' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, borderRadius: 14, padding: '14px 12px', textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: s.color }}>{s.count}</div>
            <div style={{ fontSize: 11, color: s.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* New Order */}
      {canManageOrders && <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>+ New Order Token</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Customer name (optional)"
            style={{ flex: 1, minWidth: 140, border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 13, outline: 'none', background: '#f8fafc', fontWeight: 600, color: '#1e293b' }}
          />
          <input
            value={newNote}
            onChange={e => setNewNote(e.target.value)}
            placeholder="Order note (optional)"
            style={{ flex: 2, minWidth: 180, border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 13, outline: 'none', background: '#f8fafc', fontWeight: 600, color: '#1e293b' }}
          />
          <button
            onClick={createOrder}
            disabled={creating || !outletToken}
            style={{ background: creating ? '#e2e8f0' : 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: creating ? '#94a3b8' : '#fff', border: 'none', borderRadius: 10, padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
          >
            {creating ? 'Creating...' : '🎟️ Generate Token'}
          </button>
        </div>
      </div>}

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: '#94a3b8' }}>🔍</span>
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by token (e.g. E184)"
          style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 12, padding: '10px 14px 10px 36px', fontSize: 13, outline: 'none', background: '#fff', fontWeight: 600, color: '#1e293b', boxSizing: 'border-box' }}
        />
        {searchQuery && (
          <button onClick={() => setSearchQuery('')}
            style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: '#f1f5f9', border: 'none', borderRadius: 6, width: 20, height: 20, fontSize: 11, cursor: 'pointer', color: '#64748b', fontWeight: 700 }}>
            ✕
          </button>
        )}
      </div>

      {/* Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {(['all', 'waiting', 'ready', 'collected'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)}
            style={{ border: filter === f ? '1.5px solid #6366f1' : '1.5px solid #e2e8f0', background: filter === f ? '#f5f3ff' : '#fff', color: filter === f ? '#6366f1' : '#64748b', borderRadius: 20, padding: '5px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', textTransform: 'capitalize' }}>
            {f}
          </button>
        ))}
      </div>

      {/* Orders */}
      {loading && orders.length === 0 && <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>Loading...</div>}
      {(!loading || orders.length > 0) && filtered.length === 0 && (
        <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🎟️</div>
          <div style={{ fontWeight: 700 }}>No orders yet</div>
        </div>
      )}

      {!loading && (dineInOrders.length > 0 || counterOrders.length > 0) && (
        <>
          {/* Tab switcher — only one list renders at a time, so a busy
              counter queue never buries table orders in scroll (or vice versa). */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button
              onClick={() => setOrderView('dinein')}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: '11px 14px', borderRadius: 12, cursor: 'pointer',
                border: orderView === 'dinein' ? '1.5px solid #6366f1' : '1.5px solid #e2e8f0',
                background: orderView === 'dinein' ? '#fff' : '#f8fafc',
                boxShadow: orderView === 'dinein' ? '0 2px 8px rgba(99,102,241,0.15)' : 'none',
                fontSize: 13, fontWeight: 800, color: orderView === 'dinein' ? '#4f46e5' : '#64748b',
              }}>
              🍽️ Dine-In
              <span style={{
                background: orderView === 'dinein' ? '#6366f1' : '#e2e8f0',
                color: orderView === 'dinein' ? '#fff' : '#64748b',
                borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 800,
              }}>{dineInOrders.length}</span>
            </button>
            <button
              onClick={() => setOrderView('counter')}
              style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                padding: '11px 14px', borderRadius: 12, cursor: 'pointer',
                border: orderView === 'counter' ? '1.5px solid #6366f1' : '1.5px solid #e2e8f0',
                background: orderView === 'counter' ? '#fff' : '#f8fafc',
                boxShadow: orderView === 'counter' ? '0 2px 8px rgba(99,102,241,0.15)' : 'none',
                fontSize: 13, fontWeight: 800, color: orderView === 'counter' ? '#4f46e5' : '#64748b',
              }}>
              🎟️ Counter / Pickup
              <span style={{
                background: orderView === 'counter' ? '#6366f1' : '#e2e8f0',
                color: orderView === 'counter' ? '#fff' : '#64748b',
                borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 800,
              }}>{counterOrders.length}</span>
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {orderView === 'dinein'
              ? (dineInOrders.length > 0
                  ? dineInOrders.map(renderOrderCard)
                  : <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>No dine-in orders right now</div>)
              : (counterOrders.length > 0
                  ? counterOrders.map(renderOrderCard)
                  : <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>No counter/pickup orders right now</div>)
            }
          </div>
        </>
      )}

      {/* Already Claimed Info */}
      {claimInfoModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setClaimInfoModal(null)}>
          <div style={{ background: '#fff', borderRadius: 18, padding: 24, maxWidth: 320, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>🔒</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
              {claimInfoModal.token} is already claimed
            </div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>
              Being handled by <b>{claimInfoModal.name}</b>. You can still view the order, or release it if it's actually done or was claimed by mistake.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                onClick={() => {
                  setItemsModal({
                    token: claimInfoModal.order.token,
                    customerName: claimInfoModal.order.customer_name || 'Guest',
                    items: parseOrderItems(claimInfoModal.order.order_note),
                  });
                  setClaimInfoModal(null);
                }}
                style={{ background: '#6366f1', color: '#fff', border: 'none', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                View Order
              </button>
              <button
                onClick={() => { releaseOrder(claimInfoModal.token); setClaimInfoModal(null); }}
                style={{ background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Release Claim
              </button>
              <button
                onClick={() => setClaimInfoModal(null)}
                style={{ background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Claim Name Prompt */}
      {claimPrompt && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.35)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setClaimPrompt(null)}>
          <div style={{ background: '#fff', borderRadius: 18, padding: 24, maxWidth: 300, width: '100%', boxShadow: '0 24px 60px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>Claiming {claimPrompt.token}</div>
            <input
              autoFocus
              value={claimNameInput}
              onChange={e => setClaimNameInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') confirmClaim(); }}
              placeholder="Your name"
              style={{ width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', fontSize: 14, outline: 'none', background: '#f8fafc', fontWeight: 600, color: '#1e293b', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
              <button onClick={() => setClaimPrompt(null)}
                style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmClaim} disabled={!claimNameInput.trim()}
                style={{ flex: 1, background: claimNameInput.trim() ? '#6366f1' : '#e2e8f0', color: claimNameInput.trim() ? '#fff' : '#94a3b8', border: 'none', borderRadius: 10, padding: '10px', fontSize: 13, fontWeight: 700, cursor: claimNameInput.trim() ? 'pointer' : 'default' }}>
                Claim
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Modal */}
      {qrModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setQrModal(null)}>
          <div style={{ background: '#fff', borderRadius: 20, padding: 32, maxWidth: 340, width: '100%', textAlign: 'center', boxShadow: '0 32px 80px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 20, fontWeight: 900, color: '#0f172a', marginBottom: 4 }}>Token {qrModal.token}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>Show this QR to the customer</div>
            <img src={qrModal.qr} alt="QR" style={{ width: 220, height: 220, borderRadius: 12, border: '2px solid #e2e8f0' }} />
            <div style={{ marginTop: 12, fontSize: 11, color: '#64748b', background: '#f8fafc', borderRadius: 8, padding: '8px 12px', wordBreak: 'break-all' }}>{qrModal.url}</div>
            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
              <button onClick={() => { const a = document.createElement('a'); a.href = qrModal.qr; a.download = `token_${qrModal.token}.png`; a.click(); }}
                style={{ flex: 1, background: '#f5f3ff', color: '#6366f1', border: 'none', borderRadius: 12, padding: '10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                ⬇ Download
              </button>
              <button onClick={() => setQrModal(null)}
                style={{ flex: 1, background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 12, padding: '10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
          {/* Order Items Modal */}
      {itemsModal && (() => {
        const total = itemsModal.items.length;
        const packedCount = itemsModal.items.filter((item, idx) =>
          checkedItems[`${itemsModal.token}:${idx}:${item.name}`]
        ).length;
        const pct = total === 0 ? 0 : Math.round((packedCount / total) * 100);
        const isLocked = !!claims[itemsModal.token];
        return (
          <div style={{
              position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.25)', zIndex: 100,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
              animation: modalClosing ? 'backdropFadeOut 0.2s ease forwards' : 'backdropFadeIn 0.15s ease',
            }}
            onClick={() => closeModal()}>
            <div style={{
                background: '#fff', borderRadius: 20, padding: 28, maxWidth: 400, width: '100%',
                maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.15)', border: '1px solid #f1f5f9',
                animation: modalClosing ? 'modalFadeOut 0.2s ease forwards' : 'modalFadeIn 0.18s ease',
              }}
              onClick={e => e.stopPropagation()}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                <div style={{ fontSize: 22, fontWeight: 900, color: '#6366f1', letterSpacing: 1 }}>{itemsModal.token}</div>
                <button onClick={() => closeModal()}
                  style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, width: 28, height: 28, cursor: 'pointer', color: '#64748b', fontSize: 14, fontWeight: 700 }}>
                  ✕
                </button>
              </div>
              <div style={{ fontSize: 13, color: '#64748b', fontWeight: 600, marginBottom: 10 }}>{itemsModal.customerName}</div>

              {total > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 5 }}>
                    <span>{packedCount} of {total} packed</span>
                    <span>{pct}%</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 5, background: '#f1f5f9', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', width: `${pct}%`, borderRadius: 5,
                      background: pct === 100 ? '#22c55e' : '#6366f1',
                      transition: 'width 0.25s ease, background 0.25s ease',
                    }} />
                  </div>
                </div>
              )}

            {itemsModal.items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '12px 0', color: '#94a3b8', fontSize: 13 }}>
                No item details for this order
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {itemsModal.items.map((item, idx) => {
                  const key = `${itemsModal.token}:${idx}:${item.name}`;
                  const checked = !!checkedItems[key];
                  return (
                    <div key={key} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 12px', borderRadius: 10,
                      background: checked ? '#f0fdf4' : '#f8fafc',
                      border: `1.5px solid ${checked ? '#bbf7d0' : '#e2e8f0'}`,
                      transition: 'all 0.15s',
                    }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleChecked(key)}
                        style={{ width: 18, height: 18, accentColor: '#22c55e', cursor: 'pointer', flexShrink: 0 }}
                      />

                      {isLocked ? (
                        <div style={{
                          width: 26, height: 26, borderRadius: 7, background: checked ? '#22c55e' : '#e0e7ff',
                          color: checked ? '#fff' : '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 12, fontWeight: 800, flexShrink: 0,
                        }}>
                          {item.qty}
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                          <button
                            onClick={() => changeItemQty(itemsModal.token, idx, -1)}
                            disabled={item.qty <= 1}
                            style={{ width: 20, height: 20, borderRadius: 6, border: 'none', background: '#e0e7ff', color: '#6366f1', fontSize: 13, fontWeight: 800, cursor: item.qty <= 1 ? 'default' : 'pointer', opacity: item.qty <= 1 ? 0.4 : 1 }}>
                            −
                          </button>
                          <div style={{ minWidth: 18, textAlign: 'center', fontSize: 12, fontWeight: 800, color: '#6366f1' }}>{item.qty}</div>
                          <button
                            onClick={() => changeItemQty(itemsModal.token, idx, 1)}
                            style={{ width: 20, height: 20, borderRadius: 6, border: 'none', background: '#e0e7ff', color: '#6366f1', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                            +
                          </button>
                        </div>
                      )}

                      <label style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => toggleChecked(key)}>
                        <div style={{
                          fontSize: 14, fontWeight: 600,
                          color: checked ? '#15803d' : '#1e293b',
                          textDecoration: checked ? 'line-through' : 'none',
                        }}>
                          {item.name}
                        </div>
                        {item.special.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 3 }}>
                            {item.special.map((s, i) => (
                              <span key={i} style={{ background: '#fef2f2', color: '#dc2626', fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 20, border: '1px solid #fecaca' }}>
                                ⚠ {s}
                              </span>
                            ))}
                          </div>
                        )}
                      </label>

                      {checked && (
                        <span style={{ background: '#dcfce7', color: '#15803d', fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          ✓ Already made
                        </span>
                      )}
                      {!checked && idx >= (readyItemCounts[itemsModal.token] ?? Infinity) && (
                        <span style={{ background: '#eff6ff', color: '#2563eb', fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 }}>
                          🆕 Added after Ready
                        </span>
                      )}

                      {!isLocked && (
                        <button
                          onClick={() => removeItemFromOrder(itemsModal.token, idx)}
                          title="Remove item"
                          style={{ background: '#fef2f2', border: 'none', borderRadius: 7, width: 24, height: 24, fontSize: 12, cursor: 'pointer', color: '#dc2626', flexShrink: 0 }}>
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add item — always open, claimed or not, since new items still
                need to reach the kitchen even mid-prep. */}
            <div style={{ display: 'flex', gap: 6, marginTop: itemsModal.items.length === 0 ? 4 : 10 }}>
              <input
                value={newItemDraft}
                onChange={e => setNewItemDraft(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && newItemDraft.trim()) {
                    addItemToOrder({ token: itemsModal.token, name: newItemDraft.trim(), qty: 1 });
                    setNewItemDraft('');
                  }
                }}
                placeholder="+ Add item"
                style={{ flex: 1, border: '1.5px dashed #cbd5e1', borderRadius: 10, padding: '9px 12px', fontSize: 13, outline: 'none', background: '#fff', fontWeight: 600, color: '#1e293b' }}
              />
              <button
                onClick={() => {
                  if (!newItemDraft.trim()) return;
                  addItemToOrder({ token: itemsModal.token, name: newItemDraft.trim(), qty: 1 });
                  setNewItemDraft('');
                }}
                disabled={!newItemDraft.trim()}
                style={{ background: newItemDraft.trim() ? '#6366f1' : '#e2e8f0', color: newItemDraft.trim() ? '#fff' : '#94a3b8', border: 'none', borderRadius: 10, padding: '9px 14px', fontSize: 13, fontWeight: 700, cursor: newItemDraft.trim() ? 'pointer' : 'default' }}>
                Add
              </button>
            </div>

              <div style={{ marginTop: 20 }}>
                <button
                  disabled={pct !== 100}
                  onClick={() => closeModal(() => updateStatus(itemsModal.token, 'ready'))}
                  style={{
                    width: '100%', padding: '12px 0', borderRadius: 12, border: 'none',
                    fontSize: 14, fontWeight: 800, letterSpacing: 0.5,
                    color: pct === 100 ? '#fff' : '#94a3b8',
                    background: pct === 100 ? '#22c55e' : '#f1f5f9',
                    cursor: pct === 100 ? 'pointer' : 'default',
                    animation: modalComplete ? 'readyPulse 0.65s ease' : 'none',
                    transition: 'background 0.2s ease, color 0.2s ease',
                  }}>
                  {pct === 100 ? 'READY ✓' : `READY (${total - packedCount} left)`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      <style>{`
        @keyframes tokenBlink {
          0%, 49%   { background: #dc2626; color: #ffffff; }
          50%, 100% { background: #ffffff; color: #dc2626; }
        }
        @keyframes readyPulse {
          0%   { transform: scale(1); box-shadow: 0 0 0 0 rgba(34,197,94,0.5); }
          50%  { transform: scale(1.03); box-shadow: 0 0 0 8px rgba(34,197,94,0); }
          100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(34,197,94,0); }
        }
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.96) translateY(6px); }
          to   { opacity: 1; transform: scale(1) translateY(0); }
        }
        @keyframes modalFadeOut {
          from { opacity: 1; transform: scale(1) translateY(0); }
          to   { opacity: 0; transform: scale(0.96) translateY(6px); }
        }
        @keyframes backdropFadeIn {
          from { opacity: 0; } to { opacity: 1; }
        }
        @keyframes backdropFadeOut {
          from { opacity: 1; } to { opacity: 0; }
        }
      `}</style>

      {/* Today's Report */}
      <div style={{ background: '#fff', borderRadius: 16, padding: 20, marginTop: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: '#0f172a', marginBottom: 14 }}>📊 Today's Report</div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 12 }}>
          <div style={{ background: '#f8fafc', borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#0f172a' }}>{todayOrders.length}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700 }}>Received Today</div>
          </div>
          <div style={{ background: '#f5f3ff', borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#8b5cf6' }}>{todayCollected.length}</div>
            <div style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 700 }}>Delivered Today</div>
          </div>
          <div style={{ background: '#f0fdf4', borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#22c55e' }}>{todayReady.length}</div>
            <div style={{ fontSize: 11, color: '#22c55e', fontWeight: 700 }}>Ready, Not Collected</div>
          </div>
          <div style={{ background: '#fef9ec', borderRadius: 12, padding: '12px 14px' }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#f59e0b' }}>{todayWaiting.length}</div>
            <div style={{ fontSize: 11, color: '#f59e0b', fontWeight: 700 }}>Still Waiting</div>
          </div>
        </div>

        {(avgPrepMins !== null || avgTotalMins !== null) && (
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', marginTop: 14, paddingTop: 14 }}>
            {avgPrepMins !== null && (
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>{avgPrepMins.toFixed(1)}m</div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Avg. time to Ready</div>
              </div>
            )}
            {avgTotalMins !== null && (
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 18, fontWeight: 900, color: '#0f172a' }}>{avgTotalMins.toFixed(1)}m</div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Avg. time to Delivered</div>
              </div>
            )}
          </div>
        )}

        <div style={{ fontSize: 10, color: '#cbd5e1', marginTop: 12 }}>
          Timing is only tracked from status changes made on this device — orders updated elsewhere, or before today, won't have timing data.
        </div>
      </div>

      {/* Footer */}
      <div style={{ textAlign:'center', padding:'16px 0 8px', fontSize:9, fontWeight:700, letterSpacing:2, color:'#cbd5e1' }}>
        POWERED BY CLOUDAIRAMBO™
      </div>
    </div>
  );
}
